import { useState, useRef, useEffect } from 'react'
import { Modal } from '@/shared/components/ui'
import { SearchableSelect } from '@/shared/components/ui/SearchableSelect'
import { useTranslation } from '@/shared/contexts/TranslationContext'
import { createAsset } from '@/modules/inventory/holdings/assets'
import { getPoultryGroupId } from '@/modules/poultry/poultryInventoryService'
import type { AssetOption, AssetSubgroupOption } from '@/modules/inventory/holdings'

export interface AssetFormModalProps {
  unitId: string
  isOpen: boolean
  assetSubgroups: AssetSubgroupOption[]
  initialSubgroupId?: string
  onClose: () => void
  onCreated: (asset: AssetOption) => void
}

const INPUT_CLS =
  'w-full rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-3 py-2 text-sm text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent disabled:opacity-50'

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i

export function AssetFormModal({
  unitId,
  isOpen,
  assetSubgroups,
  initialSubgroupId = '',
  onClose,
  onCreated,
}: AssetFormModalProps) {
  const { t } = useTranslation()
  const tc = (k: string) => t(`registrations.batches.assetModal.${k}`)

  const [name, setName] = useState('')
  const [subgroupId, setSubgroupId] = useState(initialSubgroupId)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const nameRef = useRef<HTMLInputElement>(null)
  const subgroupOptions = [
    { value: '', label: tc('fields.subgroupNone') },
    ...assetSubgroups.map((subgroup) => ({ value: subgroup.id, label: subgroup.name })),
  ]

  useEffect(() => {
    if (isOpen) {
      setName('')
      setSubgroupId(initialSubgroupId)
      setError(null)
      setTimeout(() => nameRef.current?.focus(), 80)
    }
  }, [initialSubgroupId, isOpen])

  async function handleSave() {
    const v = name.trim()
    if (!v) { 
      setError(tc('fields.nameRequired'))
      nameRef.current?.focus()
      return 
    }
    setSaving(true)
    setError(null)
    try {
      const groupId = await getPoultryGroupId()
      const payload = {
        unit_id: unitId,
        template_id: null,
        name: v,
        uom: 'un',
        price: null,
        group_id: groupId,
        subgroup_id: UUID_RE.test(subgroupId) ? subgroupId : null,
        active: true,
        attrs: subgroupId && !UUID_RE.test(subgroupId) ? { poultry_subgroup_id: subgroupId } : {},
      }
      const created = await createAsset(payload)
      onCreated(created as unknown as AssetOption)
      onClose()
    } catch (err: any) {
      setError(err?.message ?? tc('messages.error'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal 
      isOpen={isOpen} 
      onClose={onClose} 
      title={tc('title')}
      size="sm"
      headerRight={
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={handleSave}
            disabled={saving || !name.trim()}
            className="flex items-center gap-2 text-emerald-600 dark:text-emerald-300 hover:text-emerald-800 dark:hover:text-emerald-100 disabled:opacity-40 transition-colors"
            aria-label={tc('title')}
            title={tc('submitTitle')}
          >
            {saving ? (
              <svg className="animate-spin w-5 h-5" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
            ) : (
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            )}
          </button>
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="flex items-center gap-2 text-slate-500 hover:text-slate-700 dark:text-slate-300 dark:hover:text-white transition-colors"
            aria-label={t('actions.close')}
            title={t('actions.close')}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      }
    >
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
            {tc('fields.name')} <span className="text-red-500">*</span>
          </label>
          <input 
            ref={nameRef} 
            value={name} 
            onChange={(e) => setName(e.target.value)} 
            onKeyPress={(e) => e.key === 'Enter' && !saving && name.trim() && handleSave()}
            placeholder={tc('fields.namePlaceholder')}
            className={INPUT_CLS} 
            disabled={saving} 
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
            {tc('fields.subgroup')}
          </label>
          <SearchableSelect
            options={subgroupOptions}
            value={subgroupId}
            onChange={setSubgroupId}
            placeholder={tc('fields.subgroupPlaceholder')}
            disabled={saving}
          />
        </div>
        {error && <p className="text-sm text-rose-600">{error}</p>}
      </div>
    </Modal>
  )
}
